import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Footer from './Layout/Footer';
import Header from './Layout/Header';
import { getProfile, resetPassword, updateProfile } from '../Api/Auth';
import { getStoredUserDetail, DEFAULT_USER_AVATAR, getUserDisplayName, getUserAvatar, saveUserDetail } from '../utils/userProfile';

function Profile() {
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
    });
    const [passwordForm, setPasswordForm] = useState({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const [passwordErrors, setPasswordErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);

    const loadProfile = async () => {
        setLoading(true);
        const res = await getProfile();
        if (res.status === 1) {
            setProfile(res.payload);
            setForm({
                firstName: res.payload.firstName || '',
                lastName: res.payload.lastName || '',
                phone: res.payload.phone || '',
                email: res.payload.email || '',
            });
            const stored = getStoredUserDetail() || {};
            saveUserDetail({
                ...stored,
                ...res.payload,
                token: stored.token,
            });
        } else {
            const stored = getStoredUserDetail();
            if (stored) {
                setProfile(stored);
                setForm({
                    firstName: stored.firstName || '',
                    lastName: stored.lastName || '',
                    phone: stored.phone || '',
                    email: stored.email || '',
                });
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        document.title = 'Profile - Mindforce';
        loadProfile();
    }, []);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const setPasswordField = (field, value) => {
        setPasswordForm((prev) => ({ ...prev, [field]: value }));
        if (passwordErrors[field]) {
            setPasswordErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const validateProfileForm = () => {
        const nextErrors = {};
        if (!form.firstName.trim()) nextErrors.firstName = 'First name is required';
        if (!form.lastName.trim()) nextErrors.lastName = 'Last name is required';
        if (!form.phone.trim()) nextErrors.phone = 'Phone is required';
        return nextErrors;
    };

    const validatePasswordForm = () => {
        const nextErrors = {};
        if (!passwordForm.password) nextErrors.password = 'Password is required';
        if (!passwordForm.confirmPassword) nextErrors.confirmPassword = 'Confirm password is required';
        if (passwordForm.password && passwordForm.password.length < 8) {
            nextErrors.password = 'Password must be at least 8 characters';
        }
        if (passwordForm.password !== passwordForm.confirmPassword) {
            nextErrors.confirmPassword = 'Passwords do not match';
        }
        return nextErrors;
    };

    const handleProfileSubmit = async (event) => {
        event.preventDefault();
        const formErrors = validateProfileForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        setSavingProfile(true);
        const res = await updateProfile({
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            phone: form.phone.trim(),
        });
        setSavingProfile(false);

        if (res.status === 1) {
            setProfile(res.payload);
            const stored = getStoredUserDetail() || {};
            saveUserDetail({
                ...stored,
                ...res.payload,
                token: stored.token,
            });
            Swal.fire({
                text: res.message,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
            });
        } else {
            setErrors({ submit: res.message || 'Profile could not be updated' });
        }
    };

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();
        const formErrors = validatePasswordForm();
        if (Object.keys(formErrors).length > 0) {
            setPasswordErrors(formErrors);
            return;
        }

        setSavingPassword(true);
        const res = await resetPassword({ password: passwordForm.password });
        setSavingPassword(false);

        if (res.status === 1) {
            setPasswordForm({ password: '', confirmPassword: '' });
            Swal.fire({
                text: res.message,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
            });
        } else {
            setPasswordErrors({ submit: res.message || 'Password could not be updated' });
        }
    };

    const displayName = getUserDisplayName(profile || form);

    return (
        <div>
            <Header />
            <div id="layout-wrapper">
                <div className="main-content">
                    <div className="page-content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="page-title-right">
                                                <ol className="breadcrumb m-0">
                                                    <li className="breadcrumb-item"><span>Account</span></li>
                                                    <li className="breadcrumb-item active">Profile</li>
                                                </ol>
                                            </div>
                                            <h4 className="mb-sm-0">Profile</h4>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {loading ? (
                                <div className="card">
                                    <div className="card-body text-muted">Loading profile...</div>
                                </div>
                            ) : (
                                <div className="row">
                                    <div className="col-xl-4 col-lg-5">
                                        <div className="card">
                                            <div className="card-body text-center">
                                                <img
                                                    src={getUserAvatar(profile || form || getStoredUserDetail())}
                                                    alt={displayName}
                                                    className="rounded-circle avatar-xl img-thumbnail mb-3"
                                                    style={{ width: 96, height: 96, objectFit: 'cover' }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = DEFAULT_USER_AVATAR;
                                                    }}
                                                />
                                                <h5 className="mb-1" style={{ color: '#405189' }}>{displayName}</h5>
                                                <p className="text-muted mb-1">{form.email}</p>
                                                {profile?.phone ? (
                                                    <p className="text-muted mb-0">{profile.phone}</p>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-xl-8 col-lg-7">
                                        <div className="card">
                                            <div className="card-header border-0">
                                                <h5 className="card-title mb-0" style={{ color: '#405189' }}>Personal Details</h5>
                                            </div>
                                            <div className="card-body">
                                                {errors.submit ? (
                                                    <div className="alert alert-danger py-2">{errors.submit}</div>
                                                ) : null}
                                                <form onSubmit={handleProfileSubmit}>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">First name</label>
                                                                <input
                                                                    type="text"
                                                                    className={`form-control${errors.firstName ? ' is-invalid' : ''}`}
                                                                    value={form.firstName}
                                                                    onChange={(e) => setField('firstName', e.target.value)}
                                                                />
                                                                {errors.firstName ? (
                                                                    <div className="invalid-feedback">{errors.firstName}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">Last name</label>
                                                                <input
                                                                    type="text"
                                                                    className={`form-control${errors.lastName ? ' is-invalid' : ''}`}
                                                                    value={form.lastName}
                                                                    onChange={(e) => setField('lastName', e.target.value)}
                                                                />
                                                                {errors.lastName ? (
                                                                    <div className="invalid-feedback">{errors.lastName}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">Phone</label>
                                                                <input
                                                                    type="text"
                                                                    className={`form-control${errors.phone ? ' is-invalid' : ''}`}
                                                                    value={form.phone}
                                                                    onChange={(e) => setField('phone', e.target.value)}
                                                                />
                                                                {errors.phone ? (
                                                                    <div className="invalid-feedback">{errors.phone}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">Email</label>
                                                                <input
                                                                    type="email"
                                                                    className="form-control"
                                                                    value={form.email}
                                                                    readOnly
                                                                    disabled
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={savingProfile}
                                                    >
                                                        {savingProfile ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                </form>
                                            </div>
                                        </div>

                                        <div className="card">
                                            <div className="card-header border-0">
                                                <h5 className="card-title mb-0" style={{ color: '#405189' }}>Change Password</h5>
                                            </div>
                                            <div className="card-body">
                                                {passwordErrors.submit ? (
                                                    <div className="alert alert-danger py-2">{passwordErrors.submit}</div>
                                                ) : null}
                                                <form onSubmit={handlePasswordSubmit}>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">New password</label>
                                                                <input
                                                                    type="password"
                                                                    className={`form-control${passwordErrors.password ? ' is-invalid' : ''}`}
                                                                    value={passwordForm.password}
                                                                    onChange={(e) => setPasswordField('password', e.target.value)}
                                                                />
                                                                {passwordErrors.password ? (
                                                                    <div className="invalid-feedback">{passwordErrors.password}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">Confirm password</label>
                                                                <input
                                                                    type="password"
                                                                    className={`form-control${passwordErrors.confirmPassword ? ' is-invalid' : ''}`}
                                                                    value={passwordForm.confirmPassword}
                                                                    onChange={(e) => setPasswordField('confirmPassword', e.target.value)}
                                                                />
                                                                {passwordErrors.confirmPassword ? (
                                                                    <div className="invalid-feedback">{passwordErrors.confirmPassword}</div>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={savingPassword}
                                                    >
                                                        {savingPassword ? 'Updating...' : 'Update Password'}
                                                    </button>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </div>
    );
}

export default Profile;
